import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle, Sparkle } from "lucide-react";
import { BRAND, HERO_IMG, scrollToId, waLink } from "../data/site";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.16, duration: 1.05, ease: [0.16, 1, 0.3, 1] },
  }),
};

function RevealLine({ children, i, className = "" }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span className="block will-change-transform" variants={line} initial="hidden" animate="show" custom={i}>
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 800], [0, 120]);
  const textY = useTransform(scrollY, [0, 800], [0, 60]);

  return (
    <section id="hero" data-testid="hero-section" className="grain relative min-h-screen overflow-hidden bg-cream pt-28">
      <Sparkle className="absolute left-[8%] top-[22%] h-5 w-5 text-gold animate-float-y" />
      <Sparkle className="absolute right-[38%] top-[14%] h-3 w-3 text-gold-dark animate-float-y [animation-delay:1.6s]" />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 pb-16 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-8">
        <motion.div style={{ y: textY }} className="relative z-10 text-center lg:text-left">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-6 text-[11px] font-semibold uppercase tracking-[0.35em] text-gold-dark"
          >
            {BRAND.name} · Moradabad
          </motion.p>

          <h1 className="font-serif text-[13vw] font-medium leading-[1.02] tracking-tight sm:text-7xl lg:text-[5.6rem]">
            <RevealLine i={0}>Turning</RevealLine>
            <RevealLine i={1}>Moments Into</RevealLine>
            <RevealLine i={2}>
              <span className="font-script gold-foil-text block text-[1.15em] leading-[1.1] sm:inline sm:pr-3">
                Beautiful
              </span>
              <span className="block sm:inline">Memories</span>
            </RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mx-auto mt-7 max-w-md text-base leading-relaxed text-clay lg:mx-0"
          >
            Birthdays are our speciality — plus anniversaries, baby showers and every little
            celebration in between, planned, styled and shipped with love by {BRAND.founder}.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center justify-center gap-4 lg:justify-start"
          >
            <a
              data-testid="hero-whatsapp-button"
              href={waLink(`Hello ${BRAND.name}! I'd like to plan an event.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-night shadow-[0_18px_40px_-16px_rgba(176,141,66,0.7)] transition-all hover:-translate-y-0.5 hover:bg-gold-light"
            >
              <MessageCircle className="h-4 w-4" /> Plan on WhatsApp
            </a>
            <button
              data-testid="hero-explore-button"
              onClick={() => scrollToId("services")}
              className="inline-flex items-center gap-3 rounded-full border border-gold-faint px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-all hover:border-gold hover:text-gold-dark"
            >
              Explore Services <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0)", opacity: 0.4 }}
          animate={{ clipPath: "inset(0% 0 0 0)", opacity: 1 }}
          transition={{ delay: 0.5, duration: 1.3, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-auto w-full max-w-sm lg:max-w-none"
        >
          <div className="absolute -inset-3 arch-frame border border-gold-faint" />
          <motion.div style={{ y: imgY }} className="arch-frame relative h-[52vh] overflow-hidden lg:h-[74vh]">
            <img
              src={HERO_IMG}
              alt="Golden birthday celebration by Vayu Celebrations"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-night/40 via-transparent to-transparent" />
            <p className="absolute bottom-5 left-0 right-0 text-center font-script text-2xl text-cream">
              {BRAND.tagline}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
